import React, {FC, PropsWithChildren, useCallback, useEffect, useMemo, useState} from 'react';

import {globalWindow} from '../../../../libs/shared/core/src/helpers/browser/browserHelpers';
import {TaskSourcesEnum} from '../../../../libs/shared/core/src/helpers/instrumentation/eventLoopPerformanceHelpers';
import {OnboardingTipTypesEnum, StartGuideTaskTypesEnum} from '../../../models/api/preferencesModel';
import {AppState} from '../../../store/appState';
import {AppStore} from '../../../store/appStore';
import {InfoBubblesContext, InfoBubblesContextProps, InfoBubblesMap} from './infoBubblesContext';

/*
 * Props.
 */

interface InfoBubblesProviderProps {
  /** The store used to listen for changes that should trigger an info bubble. */
  store: AppStore;
  /** The info bubbles that have already been seen by the teammate. */
  seenOnboardingTips: ReadonlyArray<OnboardingTipTypesEnum>;
  /** Handler to update teammate preference. */
  updateSeenOnboardingTips(bubbles: ReadonlyArray<OnboardingTipTypesEnum>, shouldMarkUnseen?: boolean): void;
  /** Whether the teammate tour is currently in progress. */
  isTeammateTourActive: boolean;
  /** An optional active sequence of bubbles. */
  activeSequence?: ReadonlyArray<OnboardingTipTypesEnum>;
  /** The startguide tasks that have already been completed by the teammate. */
  completedStartGuideTasks: ReadonlyArray<StartGuideTaskTypesEnum>;
  /** Whether the user is in a team context with unread conversations. */
  shouldShowReadStatusBubble: boolean;
  /** Whether the user is in a private inbox and should see a private comment bubble. */
  shouldShowPrivateCommentBubble: boolean;
  /** Whether the user has commented on a conversation and we should show an archive infoBubble. */
  shouldShowFirstArchiveInfoBubble: boolean;
  /** Whether the user is searching with a tag modifier */
  shouldShowLensTagSearchBubble: boolean;
  /** Whether the user is searching with an assignee modifier */
  shouldShowLensAssigneeSearchBubble: boolean;
}

/*
 * Component.
 */

export const InfoBubblesProvider: FC<PropsWithChildren<InfoBubblesProviderProps>> = (props) => {
  const {
    store,
    seenOnboardingTips,
    updateSeenOnboardingTips,
    activeSequence,
    isTeammateTourActive,
    completedStartGuideTasks,
    shouldShowReadStatusBubble,
    shouldShowPrivateCommentBubble,
    shouldShowFirstArchiveInfoBubble,
    shouldShowLensTagSearchBubble,
    shouldShowLensAssigneeSearchBubble,
  } = props;
  const [infoBubbles, setInfoBubbles] = useState<InfoBubblesMap>({});

  /*
   * Context methods.
   */

  /** Open an info bubble. */
  const openInfoBubble = useCallback(
    (infoBubble: OnboardingTipTypesEnum) => {
      // If we're already trying to open that infoBubble no need to run another callback/rerender.
      if (infoBubbles[infoBubble]) {
        return;
      }

      globalWindow.requestInstrumentedIdleCallback(
        () => {
          setInfoBubbles((previous) => {
            if (previous[infoBubble]) {
              return previous;
            }

            return {...previous, [infoBubble]: true};
          });
        },
        {source: TaskSourcesEnum.USER_INTERACTION},
      );
    },
    [infoBubbles],
  );

  const useOpenInfoBubbleEffect = (infoBubble: OnboardingTipTypesEnum, shouldOpenInfoBubble: boolean) =>
    useEffect(() => {
      if (seenOnboardingTips.includes(infoBubble)) {
        return;
      }

      if (shouldOpenInfoBubble) {
        openInfoBubble(infoBubble);
      }
    }, [infoBubble, shouldOpenInfoBubble]);

  /** Close an info bubble. */
  const closeInfoBubbles = useCallback(
    (...bubblesToClose: ReadonlyArray<OnboardingTipTypesEnum>) => {
      // Mark the bubble as seen.
      updateSeenOnboardingTips(bubblesToClose);

      setInfoBubbles((previous) => {
        const newBubbles = {...previous};
        bubblesToClose.forEach((bubble) => {
          newBubbles[bubble] = false;
        });

        return newBubbles;
      });
    },
    [updateSeenOnboardingTips],
  );

  /*
   * State listeners.
   */

  // Close the comment composer info bubble if the user mentions a teammate.
  const maybeCloseStartGuideCommentComposerBubble = useCallback(
    (state: AppState) => {
      const mentionedTeammate = state.data.preferences.completedStartGuideTasks.includes(
        StartGuideTaskTypesEnum.MENTION_TEAMMATE,
      );

      if (!mentionedTeammate) {
        return;
      }

      closeInfoBubbles(OnboardingTipTypesEnum.START_GUIDE_COMMENT_COMPOSER);
    },
    [closeInfoBubbles],
  );

  /** p2 read status */
  useOpenInfoBubbleEffect(OnboardingTipTypesEnum.READ_STATUS_HIGHLIGHT, shouldShowReadStatusBubble);
  useOpenInfoBubbleEffect(OnboardingTipTypesEnum.READ_STATUS_CELL, shouldShowReadStatusBubble);

  /** private message comment  */
  useOpenInfoBubbleEffect(OnboardingTipTypesEnum.PRIVATE_INBOX_COMMENT, shouldShowPrivateCommentBubble);

  /** Lenses bubble when searching for a tag */
  useOpenInfoBubbleEffect(OnboardingTipTypesEnum.LENSES_TAG_SEARCH, shouldShowLensTagSearchBubble);

  /** Lenses bubble when searching for an assignee */
  useOpenInfoBubbleEffect(OnboardingTipTypesEnum.LENSES_ASSIGNEE_SEARCH, shouldShowLensAssigneeSearchBubble);

  // Open the archive info bubble if a user comments for the first time
  useEffect(() => {
    if (!shouldShowFirstArchiveInfoBubble || infoBubbles[OnboardingTipTypesEnum.FIRST_CONVERSATION_ARCHIVE]) {
      return;
    }

    openInfoBubble(OnboardingTipTypesEnum.FIRST_CONVERSATION_ARCHIVE);
  }, [openInfoBubble, shouldShowFirstArchiveInfoBubble, infoBubbles]);

  useEffect(() => {
    // If neither the guided demo nor the teammate tour are active, check if we need to watch for changes in startguide comment composer task.
    if (!isTeammateTourActive) {
      if (!completedStartGuideTasks.includes(StartGuideTaskTypesEnum.MENTION_TEAMMATE)) {
        return store.subscribe(() => {
          const state = store.getState();
          const shouldWatchStateToCloseInfoBubble = (infoBubble: OnboardingTipTypesEnum) =>
            !isInfoBubbleUnseen(infoBubbles, state)(infoBubble);

          if (shouldWatchStateToCloseInfoBubble(OnboardingTipTypesEnum.START_GUIDE_COMMENT_COMPOSER)) {
            maybeCloseStartGuideCommentComposerBubble(state);
          }
        });
      }

      return undefined;
    }

    // The active sequence includes only unseen bubbles in the sequence, so if there is one, we should open the first entry.
    // This allows bubbles to open "automatically" once the prior bubble in the sequence is closed.
    if (activeSequence && !infoBubbles[activeSequence[0]]) {
      openInfoBubble(activeSequence[0]);
    }

    return undefined;
  }, [
    store,
    infoBubbles,
    activeSequence,
    openInfoBubble,
    isTeammateTourActive,
    maybeCloseStartGuideCommentComposerBubble,
    completedStartGuideTasks,
  ]);

  const context = useMemo<InfoBubblesContextProps>(
    () => ({infoBubbles, openInfoBubble, closeInfoBubbles}),
    [closeInfoBubbles, infoBubbles, openInfoBubble],
  );

  return <InfoBubblesContext.Provider value={context}>{props.children}</InfoBubblesContext.Provider>;
};

/*
 * Helpers.
 */

function isInfoBubbleUnseen(infoBubbles: InfoBubblesMap, state: AppState) {
  // An info bubble is unseen if it has not been saved to `seenOnboardingTips`, and is not currently open.
  return (infoBubble: OnboardingTipTypesEnum): boolean =>
    !state.data.preferences.seenOnboardingTips.includes(infoBubble) && !infoBubbles[infoBubble];
}
